import React, { useState, useEffect, useRef } from 'react';
import apiClient from '../api/client';
import PlatformIcon from './PlatformIcon';
import { useAuth } from '../context/AuthContext';

const ALL_PLATFORM_OPTIONS = [
  { key: 'twitch', label: 'Twitch' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'kick', label: 'Kick' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'instagram', label: 'Instagram', adminOnly: true },
  { key: 'twitter', label: 'Twitter/X' },
];

const ContentScannerModal = ({ campaignId, isOpen, onClose, onScanComplete }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const PLATFORM_OPTIONS = ALL_PLATFORM_OPTIONS.filter(p => !p.adminOnly || isAdmin);
  const DEFAULT_SELECTED = PLATFORM_OPTIONS.map(p => p.key);

  const [loading, setLoading] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState(DEFAULT_SELECTED);
  const pollIntervalRef = useRef(null);

  // Cleanup polling on unmount or close
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const startProgressPolling = () => {
    // Poll every 2 seconds for progress updates
    pollIntervalRef.current = setInterval(async () => {
      try {
        const response = await apiClient.get(`/scan/campaign/${campaignId}/progress`, { timeout: 30000 });
        const progressData = response.data;

        // If we're actively scanning but get 'idle', keep last known progress
        // (can happen briefly during phase transitions or if cleanup timer from previous scan fires)
        if (progressData.status === 'idle') {
          return; // Skip this update, keep showing last progress
        }

        setProgress(progressData);

        // Detect completion from polling
        if (progressData.status === 'complete') {
          stopPolling();
          setLoading(false);
          setScanResults({
            found: progressData.foundContent || 0,
            added: progressData.added || 0,
            errors: progressData.errors || 0,
            foundContent: progressData.foundContentList || []
          });
          if (onScanComplete) {
            onScanComplete();
          }
        } else if (progressData.status === 'error') {
          stopPolling();
          setLoading(false);
          setError(progressData.message || 'Scan failed');
        }
      } catch (err) {
        console.error('Error polling progress:', err);
      }
    }, 2000);
  };

  const togglePlatform = (platform) => {
    setSelectedPlatforms(prev => {
      if (prev.includes(platform)) {
        // Don't allow deselecting all platforms
        if (prev.length === 1) return prev;
        return prev.filter(p => p !== platform);
      }
      return [...prev, platform];
    });
  };

  const handleScan = async () => {
    try {
      setLoading(true);
      setError('');
      setScanResults(null);
      setProgress({ status: 'starting', progress: 0, message: 'Initializing scan...' });

      // Start polling for progress
      startProgressPolling();

      // Fire-and-forget: POST returns immediately, scan runs in background
      // Override default 10s timeout - server loads 230+ creators before responding
      await apiClient.post(`/scan/campaign/${campaignId}`, {
        platforms: selectedPlatforms
      }, { timeout: 60000 });

      // Don't set results here - polling will detect completion
    } catch (err) {
      console.error('Scan error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to start scan');
      setProgress(null);
      setLoading(false);
      stopPolling();
    }
  };

  const handleCancel = async () => {
    try {
      await apiClient.post(`/scan/campaign/${campaignId}/cancel`);
      stopPolling();
      setLoading(false);
      setProgress({ status: 'cancelled', message: 'Scan cancelled' });
    } catch (err) {
      console.error('Error cancelling scan:', err);
    }
  };

  const handleClose = () => {
    stopPolling();
    setScanResults(null);
    setProgress(null);
    setError('');
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h2 className="text-2xl font-bold mb-4">Scan for Content</h2>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
            {error}
          </div>
        )}

        {scanResults ? (
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded">
              <h3 className="font-semibold mb-2">Scan Complete!</h3>
              <p className="text-sm mb-1">
                <strong>Found:</strong> {scanResults.found || 0} content items
              </p>
              <p className="text-sm mb-1">
                <strong>Added:</strong> {scanResults.added || 0} new items
              </p>
              {scanResults.errors > 0 && (
                <p className="text-sm text-red-600">
                  <strong>Errors:</strong> {scanResults.errors}
                </p>
              )}
            </div>

            {/* Found Content List */}
            {scanResults.foundContent && scanResults.foundContent.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 border-b">
                  <h4 className="font-medium text-sm text-gray-700">New Content Found</h4>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {scanResults.foundContent.map((content, index) => (
                    <a
                      key={content.id || index}
                      href={content.contentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-2 hover:bg-gray-50 border-b last:border-b-0 transition"
                    >
                      {content.thumbnailUrl ? (
                        <img
                          src={content.thumbnailUrl}
                          alt=""
                          className="w-16 h-10 object-cover rounded flex-shrink-0"
                        />
                      ) : (
                        <div className="w-16 h-10 bg-gray-200 rounded flex-shrink-0 flex items-center justify-center">
                          <span className="text-gray-400 text-xs">
                            {content.platform?.charAt(0).toUpperCase() || '?'}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {content.title || 'Untitled'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {content.creatorName} • {content.platform} • {content.views?.toLocaleString() || 0} views
                        </p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={handleClose}
              className="w-full bg-sc-pink hover:bg-sc-pink/90 text-white font-medium py-2 px-4 rounded transition"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-gray-600">
              Scan this campaign's creators' profiles for new content matching the campaign game.
            </p>

            {/* Platform Selector */}
            {!loading && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Platforms to scan</label>
                <div className="flex flex-wrap gap-2">
                  {PLATFORM_OPTIONS.map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => togglePlatform(key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition border ${
                        selectedPlatforms.includes(key)
                          ? 'bg-sc-pink/10 text-sc-pink border-sc-pink/30'
                          : 'bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <PlatformIcon platform={key} size="sm" />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
                {(selectedPlatforms.includes('tiktok') || selectedPlatforms.includes('instagram') || selectedPlatforms.includes('twitter')) && (
                  <p className="text-xs text-amber-600 mt-2">
                    TikTok/Instagram/Twitter scans are slower (browser-based). Deselect them for a faster scan.
                  </p>
                )}
              </div>
            )}

            {/* Progress Bar */}
            {loading && progress && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{progress.message || 'Scanning...'}</span>
                  <span>{progress.progress || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-sc-pink h-3 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${progress.progress || 0}%` }}
                  />
                </div>
                {progress.creatorsTotal > 0 && (
                  <div className="text-xs text-gray-500 flex justify-between">
                    <span>
                      Creators: {progress.creatorsScanned || 0} / {progress.creatorsTotal}
                    </span>
                    <span>
                      Found: {progress.foundContent || 0} items
                    </span>
                  </div>
                )}
                {progress.phase && progress.phase !== 'initializing' && (
                  <div className="text-xs text-gray-400">
                    Phase: {progress.phase === 'adding_content' ? 'Adding content' :
                           progress.phase === 'complete' ? 'Complete' :
                           progress.phase === 'twitch_youtube' ? (
                             selectedPlatforms.includes('twitch') && selectedPlatforms.includes('youtube') ? 'Twitch/YouTube' :
                             selectedPlatforms.includes('twitch') ? 'Twitch' :
                             selectedPlatforms.includes('youtube') ? 'YouTube' : 'API scan'
                           ) :
                           progress.phase === 'tiktok_instagram' ? (
                             selectedPlatforms.includes('tiktok') && selectedPlatforms.includes('instagram') ? 'TikTok/Instagram' :
                             selectedPlatforms.includes('tiktok') ? 'TikTok' :
                             selectedPlatforms.includes('instagram') ? 'Instagram' : 'Browser scan'
                           ) :
                           progress.phase === 'twitter' ? 'Twitter/X' :
                           progress.phase === 'kick' ? 'Kick (live check + relink)' :
                           progress.phase}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={loading ? handleCancel : handleClose}
                className={`flex-1 px-4 py-2 rounded font-medium transition ${
                  loading
                    ? 'text-red-700 bg-red-100 hover:bg-red-200'
                    : 'text-gray-700 bg-gray-200 hover:bg-gray-300'
                }`}
              >
                {loading ? 'Cancel Scan' : 'Cancel'}
              </button>
              <button
                onClick={handleScan}
                className="flex-1 px-4 py-2 bg-sc-pink hover:bg-sc-pink/90 text-white rounded font-medium transition disabled:opacity-50"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin">⚙️</span>
                    Scanning...
                  </span>
                ) : (
                  'Start Scan'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentScannerModal;
